<template>
  <div v-if="preview" class="affiche-preview-only">
    <SupportCanvas
      v-if="supportStore.currentSupport"
      :support="supportStore.currentSupport"
      :preview="preview"
    />
    <div v-else class="flex items-center justify-center h-64">
      <p class="text-gray-500">
        Chargement du support...
      </p>
    </div>
  </div>
  <div v-else class="affiche-view flex h-[calc(100vh-80px)]">
    <!-- Détecteur de changement de marque -->
    <BrandChangeDetector :support-id="route.params.id" />
    
    <!-- Gestionnaire de jobs PDF -->
    <PdfJobManager />

    <!-- Overlay de chargement -->
    <div v-if="isSaving" class="pdf-loading-overlay">
      <div class="pdf-loading-content loader-standard">
        <div class="spinner" />
        <div class="loading-text">
          Enregistrement en cours…
        </div>
      </div>
    </div>

    <!-- Panneau latéral -->
    <ProductsPanel />

    <!-- Zone principale -->
    <div class="flex-1 bg-gray-100 p-8 overflow-auto">
      <div class="max-w-5xl mx-auto">
        <!-- En-tête -->
        <div class="mb-8 flex items-center justify-between">
          <h1 class="text-2xl font-bold text-gray-900">
            {{
              isNewSupport ? "Création d'une affiche" : "Édition d'une affiche"
            }}
          </h1>
          <div class="flex items-center gap-4">
            <button
              class="px-4 py-2 bg-brand text-white rounded-md shadow-sm hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all"
              :disabled="isSaving"
              @click="handleSaveAndGeneratePDF"
            >
              <i v-if="isSaving" class="fas fa-spinner fa-spin" />
              <span>{{ isSaving ? "Enregistrement..." : "Enregistrer" }}</span>
            </button>
          </div>
        </div>

        <!-- Configuration -->
        <!-- Canvas -->
        <SupportCanvas
          v-if="supportStore.currentSupport"
          ref="supportCanvasRef"
          :support="supportStore.currentSupport"
          :preview="preview"
        />
        <div v-else class="flex items-center justify-center h-64">
          <p class="text-gray-500">
            Chargement du support...
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import BrandChangeDetector from "@/components/supports/BrandChangeDetector.vue";
import ProductsPanel from "@/components/supports/panels/ProductsPanel.vue";
import PdfJobManager from "@/components/supports/PdfJobManager.vue";
import SupportCanvas from "@/components/supports/SupportCanvas.vue";
import { usePdfJobManager } from "@/composables/supports/usePdfJobManager";
import { useHtmlDownload } from "@/composables/supports/useHtmlDownload";
import { useSupportStore } from "@/stores/supports/useSupportStore";
import { computed, onMounted, ref, watch } from "@vue/runtime-core";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toastification";

const router = useRouter();
const toast = useToast();
const successSound = new Audio("/sounds/success.wav");
const errorSound = new Audio("/sounds/error.mp3");

const playSound = (sound: HTMLAudioElement) => {
  sound.currentTime = 0; // Réinitialiser le son
  sound.play().catch((error) => {
    console.warn("Erreur lors de la lecture du son:", error);
  });

  // Couper le son après 200ms pour le son iPhone
  setTimeout(() => {
    sound.pause();
    sound.currentTime = 0;
  }, 400);
};
// Ajuster le volume des sons
successSound.volume = 0.3; // 30% du volume pour le son iPhone
errorSound.volume = 0.15; // 15% du volume pour l'erreur

const props = defineProps<{ preview?: boolean }>();

const route = useRoute();
const supportStore = useSupportStore();
const { requestHighQualityPdf: requestHqPdf } = usePdfJobManager();
const isNewSupport = computed(
  () => !route.params.id || route.params.id === "nouveau"
);

const supportCanvasRef = ref();
const isSaving = ref(false);
const { checkForHtmlDownload } = useHtmlDownload(supportCanvasRef);

// Fonction pour initialiser le support
const initializeSupport = async () => {
  if (props.preview) return;

  supportStore.resetStore();

  if (isNewSupport.value) {
    await supportStore.initSupport("affiche");
  } else {
    try {
      await supportStore.loadSupport("affiche", route.params.id as string);
    } catch (error) {
      console.error("Erreur lors du chargement du support:", error);
    }
  }
};

watch(
  () => route.fullPath,
  () => {
    initializeSupport();
  }
);

onMounted(() => {
  initializeSupport();
  // Vérifier si on doit télécharger le HTML automatiquement
  checkForHtmlDownload();
});

// Fonction factorisée pour générer le PDF à partir du composant affiché
const generateAndGetPdfBlob = async (): Promise<Blob> => {
  // Synchronisation du store avec les données du composant
  if (
    supportCanvasRef.value &&
    typeof supportCanvasRef.value.getCurrentState === "function"
  ) {
    const currentState = supportCanvasRef.value.getCurrentState();
    if (currentState) {
      // Met à jour le store avec l'état courant du composant (pages, produits, pictos, etc.)
      supportStore.updateSupport(currentState);
    }
  }
  let previewWasActivated = false;
  if (!supportStore.isPreview) {
    supportStore.togglePreview();
    previewWasActivated = true;
    await new Promise((resolve) => setTimeout(resolve, 500));
  }
  const pdfBlob = await supportCanvasRef.value.generatePdfBlob();
  if (previewWasActivated) {
    supportStore.togglePreview();
  }
  return pdfBlob;
};

const handleSaveAndGeneratePDF = async () => {
  try {
    isSaving.value = true;
    // 1. Sauvegarder le support d'abord (le PDF est généré côté serveur).
    const response = await supportStore.saveSupport(undefined, { skipAutoHqPdf: true });
    if (response?.data?.id) {
      supportStore.currentSupport.id = response.data.id;
    }

    // 2. FormData (sans PDF : le serveur génère le vectoriel ; capture en fallback)
    const formData = new FormData();
    formData.append("supportId", supportStore.currentSupport.id.toString());
    formData.append("brandSlug", localStorage.getItem("brand") || "hv");
    formData.append(
      "supportType",
      supportStore.currentSupport.type || supportStore.supportType
    );

    // 3. Persistance du PDF (vectoriel async + notification ; capture en fallback)
    await supportStore.persistSupportPdf(
      formData,
      () => generateAndGetPdfBlob(),
      // HTML EXACT de l'éditeur -> rendu vectoriel fidèle au pixel côté serveur.
      () => supportCanvasRef.value.generateFullHTML()
    );

    playSound(successSound);
    toast.success(response.data.message || "Support sauvegardé avec succès");
    
    // 5. Lancer la génération PDF HQ APRÈS avoir désactivé le loader
    if (supportStore.currentSupport?.id) {
      // Utiliser un petit délai pour s'assurer que l'interface est mise à jour
      setTimeout(() => {
        requestHqPdf(
          supportStore.currentSupport.id, 
          supportStore.currentSupport.name || "Support sans nom"
        ).catch((error) => {
          console.error("Erreur lors de la génération PDF HQ:", error);
        });
      }, 100);
    }
    
    router.push("/documents");
  } catch (error) {
    console.error("Erreur lors de la sauvegarde et génération du PDF:", error);
    toast.error("Une erreur est survenue lors de la sauvegarde du support");
    // Ne pas re-throw l'erreur, juste la logger
  } finally {
    isSaving.value = false;
  }
};

// Nouvelle méthode pour lancer la génération PDF haute qualité
const requestHighQualityPdf = async () => {
  if (!supportStore.currentSupport?.id) {
    toast.error("Veuillez d'abord enregistrer le support avant de générer un PDF haute qualité");
    return;
  }

  try {
    await requestHqPdf(
      supportStore.currentSupport.id, 
      supportStore.currentSupport.name || "Affiche sans nom"
    );
  } catch (error) {
    console.error("Erreur lors de la demande de génération PDF HQ:", error);
  }
};
</script>

<script lang="ts">
export default {
  name: "AfficheView",
};
</script>

<style scoped>
/* Styles pour l'impression et l'aperçu */
@media print {
  .support-preview {
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
  }
}

.affiche-preview-only {
  min-height: 100vh;
  background: #f5f6f8;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Styles pour l'overlay de chargement */
.pdf-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.pdf-loading-content {
  text-align: center;
}

.loader-standard {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.spinner {
  width: 56px;
  height: 56px;
  border: 6px solid #e5e7eb;
  border-top: 6px solid #b70018;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1.5rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 1.25rem;
  color: #2d3a3a;
  font-weight: 600;
  letter-spacing: 0.02em;
}
</style>
